// Time-window computation for Overview's period tabs (посл. 3д/7д/30д/
// текущий месяц/прошлый месяц/текущий квартал/прошлый квартал/текущий
// год/12м/всё) — replaces the old 1д/7д/30д/всё range enum + scroll-
// triggered "load more history" pagination with a fixed, explicitly-
// bounded [from, to) window the user pages through with ←/→ arrows
// instead of scrolling.
//
// Two families, both MSK-anchored, both block-aligned (not exact-instant
// rolling) — user request: "3д/7д/30д — это полные дни ... начиная с
// 00:00 МСК", e.g. "посл. 3д" = today + yesterday + the day before,
// each a FULL calendar day, not "the last 72 hours from right now":
//  - d3/d7/m12 etc.: a fixed-width block of N whole MSK days/months,
//    offset=0 = the block ending TODAY (its own upper bound clamped to
//    `now` since today itself isn't over), sliding back by exactly its
//    own width per arrow click — "посл. 7д" then ← means "the 7 DAYS
//    before that", always landing on a day boundary.
//  - Calendar-aligned (month/quarter/year): a real calendar unit — "тек.
//    месяц"/"прошлый месяц" are the SAME periodType at offset 0/-1, and
//    the arrows just move that offset — so having both as separate tabs is
//    two named shortcuts into one continuous, arrow-navigable timeline,
//    not two unrelated modes.
// "all" has no offset concept — arrows are disabled for it in the UI.

const MSK_OFFSET_MS = 3 * 60 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;

// MSK-local calendar Y/M/D for a given instant — same "shift then read UTC
// fields" trick PriceChart.tsx's mskDayBoundaries already uses. Moscow has
// been a fixed UTC+3 with no DST since 2014, so this is exact, not an
// approximation good only most of the time.
function mskYmd(ms: number): { y: number; m: number; d: number } {
  const shifted = new Date(ms + MSK_OFFSET_MS);
  return { y: shifted.getUTCFullYear(), m: shifted.getUTCMonth(), d: shifted.getUTCDate() };
}

// UTC instant of MSK midnight for the given MSK-local calendar date. `m`
// deliberately allowed outside [0,11] — Date.UTC's own month-overflow
// normalization (m=-1 carries into the prior December, m=12 into next
// January, etc.) is exactly the "add/subtract N calendar months" behavior
// month/quarter/year below need, with no manual carry arithmetic.
function mskCalendarStart(y: number, m: number, d: number): number {
  return Date.UTC(y, m, d, 0, 0, 0) - MSK_OFFSET_MS;
}

// Shifts an instant by whole calendar months, preserving MSK-local
// day/time-of-day — used by "12м"'s rolling (not calendar-aligned) window.
// Every real call here shifts by a multiple of 12 from "now", so the one
// known JS Date.UTC day-of-month-overflow quirk (e.g. Mar 31 minus 1 month
// rolling into early April instead of clamping to Feb 28) essentially never
// comes up — the day-of-month never changes, only the year does.
function mskAddMonths(ms: number, deltaMonths: number): number {
  const shifted = new Date(ms + MSK_OFFSET_MS);
  return (
    Date.UTC(
      shifted.getUTCFullYear(),
      shifted.getUTCMonth() + deltaMonths,
      shifted.getUTCDate(),
      shifted.getUTCHours(),
      shifted.getUTCMinutes(),
      shifted.getUTCSeconds(),
    ) - MSK_OFFSET_MS
  );
}

export type PeriodType = "d3" | "d7" | "d30" | "month" | "quarter" | "year" | "m12" | "all";

export interface PeriodTab {
  key: string;
  label: string;
  periodType: PeriodType;
  initialOffset: number;
}

export const PERIOD_TABS: PeriodTab[] = [
  { key: "d3", label: "посл. 3д", periodType: "d3", initialOffset: 0 },
  { key: "d7", label: "посл. 7д", periodType: "d7", initialOffset: 0 },
  { key: "d30", label: "посл. 30д", periodType: "d30", initialOffset: 0 },
  { key: "month_cur", label: "тек. месяц", periodType: "month", initialOffset: 0 },
  { key: "month_prev", label: "прошлый месяц", periodType: "month", initialOffset: -1 },
  { key: "quarter_cur", label: "тек. квартал", periodType: "quarter", initialOffset: 0 },
  { key: "quarter_prev", label: "прошлый квартал", periodType: "quarter", initialOffset: -1 },
  { key: "year_cur", label: "тек. год", periodType: "year", initialOffset: 0 },
  { key: "m12", label: "12м", periodType: "m12", initialOffset: 0 },
  { key: "all", label: "всё", periodType: "all", initialOffset: 0 },
];

const ROLLING_SPAN_DAYS: Record<"d3" | "d7" | "d30", number> = { d3: 3, d7: 7, d30: 30 };

// Predates any real trading data — simpler than a dedicated "earliest row"
// backend query for what's ultimately just "don't filter at all".
const EPOCH_START_MS = Date.UTC(2020, 0, 1);

export function computeWindow(periodType: PeriodType, offset: number, nowMs: number): { from: Date; to: Date } {
  switch (periodType) {
    case "d3":
    case "d7":
    case "d30": {
      // Block of N whole MSK calendar days ending TODAY at offset=0 — e.g.
      // "посл. 3д" = today + yesterday + the day before, each starting at
      // 00:00 MSK, not "the last 72 hours from this exact second". Same
      // block-aligned-with-now-clamp shape as month/quarter/year below,
      // just parameterized by a day count instead of a calendar unit.
      const days = ROLLING_SPAN_DAYS[periodType];
      const { y, m, d } = mskYmd(nowMs);
      const todayStart = mskCalendarStart(y, m, d);
      const blockMs = days * DAY_MS;
      const from = todayStart - (days - 1) * DAY_MS + offset * blockMs;
      const to = todayStart + DAY_MS + offset * blockMs;
      return { from: new Date(from), to: new Date(Math.min(to, nowMs)) };
    }
    case "m12": {
      const to = mskAddMonths(nowMs, offset * 12);
      const from = mskAddMonths(to, -12);
      return { from: new Date(from), to: new Date(Math.min(to, nowMs)) };
    }
    case "month": {
      const { y, m } = mskYmd(nowMs);
      const from = mskCalendarStart(y, m + offset, 1);
      const to = mskCalendarStart(y, m + offset + 1, 1);
      return { from: new Date(from), to: new Date(Math.min(to, nowMs)) };
    }
    case "quarter": {
      const { y, m } = mskYmd(nowMs);
      const qStart = Math.floor(m / 3) * 3;
      const from = mskCalendarStart(y, qStart + offset * 3, 1);
      const to = mskCalendarStart(y, qStart + offset * 3 + 3, 1);
      return { from: new Date(from), to: new Date(Math.min(to, nowMs)) };
    }
    case "year": {
      const { y } = mskYmd(nowMs);
      const from = mskCalendarStart(y + offset, 0, 1);
      const to = mskCalendarStart(y + offset + 1, 0, 1);
      return { from: new Date(from), to: new Date(Math.min(to, nowMs)) };
    }
    case "all":
      return { from: new Date(EPOCH_START_MS), to: new Date(nowMs) };
  }
}

// Offsets are always <= 0 by construction (0 = the current/most-recent
// window) — forward navigation just steps toward 0 and stops there; there's
// no "future" period to page into.
export function canStepForward(offset: number): boolean {
  return offset < 0;
}

// Does the selected [from, to) window cover today's MSK calendar date?
// Overview's "Открытая позиция" widget only makes sense for a window that
// actually reaches the present — an open position paged into "прошлый
// месяц" isn't "open" in that historical window, it just hasn't closed
// YET (a fact about now, not about July). Deliberately NOT `window.to >=
// nowMs` — `to` is already clamped to `min(rawTo, nowMs)` by computeWindow
// (see its own comment), so that check would be true only in the single
// instant nowMs was captured and false a moment later on every window whose
// raw (unclamped) end is in the future — i.e. wrong for exactly the
// offset=0 case this needs to recognize reliably. Interval overlap against
// today's own [todayStart, todayStart+DAY) instead — correct regardless of
// clamping and independent of exactly when `now` is read.
export function includesToday(window: { from: Date; to: Date }, nowMs: number): boolean {
  const { y, m, d } = mskYmd(nowMs);
  const todayStart = mskCalendarStart(y, m, d);
  const todayEnd = todayStart + DAY_MS;
  return window.from.getTime() < todayEnd && window.to.getTime() > todayStart;
}
